import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiPrinter } from "react-icons/fi";
import { formatDateNumeric as tzDateNumeric } from "../../utils/datetime";
import { formatPercent, formatWholeAmount } from "../../utils/amountFormat";

/**
 * LabInvoiceModal — the single, shared invoice used everywhere in the lab app
 * (Order Management action column + Appointment Detail print button).
 * Consolidating into one component guarantees the two entry points can never
 * drift in layout again.
 *
 * Totals policy: prefer the backend `order.invoice.*` snapshot (what the patient
 * was billed) using `??` so a legitimate 0 is respected; fall back to a
 * tests-based recompute (`pct% of subtotal + delivery`) only when no backend
 * invoice exists.
 *
 * Display parity fixes vs the old inline modals:
 *   - per-test location + the header Type/Location are formatted (ON_SITE →
 *     "On-Site", HOME_VISIT → "Home Visit") instead of printing the raw code.
 *   - service time is shown in 12-hour format (e.g. "18:00:00" → "6:00 PM").
 *
 * Props:
 *   open                 boolean — controls visibility
 *   onClose              () => void
 *   order                full order object (patient, invoice/tests, payment…)
 *   centerProfile        lab profile for the header (optional; fetched if absent)
 *   adminFeePercentage   number (optional; only used for the no-backend fallback)
 */
// Every money amount on the invoice is rendered through this helper so the "IQD" prefix is
// applied consistently and can never be forgotten on a new row. IQD has no minor unit, so
// amounts are rounded to a whole number (e.g. a 6% admin fee of 1,200.3 shows as 1,200).
const iqd = (v) => `IQD ${formatWholeAmount(v)}`;

const parsePrice = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return isNaN(n) ? 0 : n;
};

// ON_SITE / VISIT_CENTER → "On-Site", HOME_VISIT → "Home Visit" (shared format).
const formatLocation = (location) => {
  if (!location) return "N/A";
  const map = {
    HOME_VISIT: "Home Visit",
    VISIT_CENTER: "On-Site",
    ON_SITE: "On-Site",
    "VISIT CENTER": "On-Site",
    "HOME VISIT": "Home Visit",
    "ON-SITE": "On-Site",
  };
  return map[String(location).toUpperCase()] || location;
};

// "HH:MM[:SS]" → 12-hour label (e.g. "18:00:00" → "6:00 PM").
const formatTime12h = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return "N/A";
  if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
  const [h, m] = timeStr.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return "N/A";
  const period = h >= 12 ? "PM" : "AM";
  const displayHour = h % 12 || 12;
  return `${displayHour}:${String(m).padStart(2, "0")} ${period}`;
};

export default function LabInvoiceModal({
  open,
  onClose,
  order,
  centerProfile,
  adminFeePercentage,
}) {
  const { i18n } = useTranslation();
  const isInvoiceAr = i18n.language === "ar";
  const invoiceRef = useRef(null);

  // Center profile: use the prop when provided, otherwise fetch it once.
  const [fetchedProfile, setFetchedProfile] = useState(null);
  useEffect(() => {
    if (!open || centerProfile) return;
    let active = true;
    (async () => {
      try {
        const token = sessionStorage.getItem("usr_1a2b3c");
        const baseURL = process.env.REACT_APP_API_URL || "";
        const res = await fetch(`${baseURL}/api/v1/lab/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (active && json?.data) setFetchedProfile(json.data);
      } catch (e) {
        /* header falls back to the letter avatar */
      }
    })();
    return () => {
      active = false;
    };
  }, [open, centerProfile]);

  const profile = centerProfile || fetchedProfile;

  const invoice = useMemo(() => {
    const bi = order?.invoice || null;
    const tests = bi?.tests || order?.tests || [];
    const subtotalCalc = tests.reduce((s, t) => s + parsePrice(t.price), 0);
    const loc = order?.serviceLocation || order?.preferredLocation || "";
    const hasHomeVisit =
      tests.some((t) => t.location === "HOME_VISIT") ||
      /home\s*visit/i.test(loc);
    const pct =
      bi?.admin_fee_percentage ??
      order?.admin_fee_percentage ??
      (adminFeePercentage != null ? Number(adminFeePercentage) : 0);
    // Prefer backend values (respecting a legitimate 0 via ??); else recompute.
    const testsTotal = bi?.subtotal ?? subtotalCalc;
    const transportationFee = bi?.transportation_fee ?? 0;
    const adminFee =
      bi?.admin_fee ?? Math.round((testsTotal + transportationFee) * (pct / 100));

    // Loyalty points the patient redeemed. Persisted on the booking
    // (`lab_bookings.loyalty_discount` / `.loyalty_points_used`) and returned on the `invoice`
    // block; the gateway only backfills it from payment metadata for older bookings.
    const loyaltyDiscount = Number(
      bi?.loyalty_discount ?? order?.loyaltyDiscount ?? 0,
    );
    const loyaltyPointsUsed = Number(
      bi?.loyalty_points_used ?? order?.loyaltyPointsUsed ?? 0,
    );
    // The two other deductions that can sit between the bill and what the patient pays,
    // both supplied by the gateway's order-details enrichment: the promo code discount and,
    // for an INSURANCE-method payment, the insurance deduction applied at checkout.
    const promoDiscount = Number(bi?.promo_discount || 0);
    const promoCode = bi?.promo_code || null;
    const insuranceCoverage = Number(bi?.insurance_coverage || 0);

    // Subtotal shown on the invoice = tests + transportation fee. That is EXACTLY the base the
    // admin-fee percentage is charged on (backend: adminFee = (subtotal + transportationFee) × %),
    // so a reader can verify the admin-fee line themselves. Mirrors the doctor invoice.
    const subtotal = testsTotal + transportationFee;

    // Total is DERIVED from the lines printed above it, exactly like the doctor invoice:
    // Subtotal + Admin Fee − Insurance Coverage − Loyalty Discount − Promo Discount.
    // It used to print the payment row's `amount_paid` instead, which is net of the promo
    // and of the insurance deduction — neither of which had a line — so a paid invoice with
    // a promo or insurance did not add up (e.g. 10,000 + 500 − 3,000 loyalty printed a Total
    // of 6,500 because a 1,000 promo was invisible). Every deduction now has its own line,
    // so the derived Total equals the amount charged.
    const total = Math.max(
      0,
      subtotal + adminFee - insuranceCoverage - loyaltyDiscount - promoDiscount,
    );

    return {
      tests,
      testsTotal,
      subtotal,
      transportationFee,
      adminFee,
      loyaltyDiscount,
      loyaltyPointsUsed,
      promoDiscount,
      promoCode,
      insuranceCoverage,
      total,
      pct,
      hasHomeVisit,
      hasBackend: !!bi,
    };
  }, [order, adminFeePercentage]);

  const invL = isInvoiceAr
    ? {
        preview: "معاينة الفاتورة",
        print: "طباعة",
        close: "إغلاق",
        invoice: "فاتورة",
        tagline: "منصة الصحة الإلكترونية",
        patientDetails: "تفاصيل المريض",
        name: "الاسم",
        mobile: "الهاتف",
        ageGender: "العمر / الجنس",
        yr: "سنة",
        serviceDetails: "تفاصيل الخدمة",
        typeLocation: "النوع / الموقع",
        serviceDate: "تاريخ الخدمة",
        serviceTime: "وقت الخدمة",
        no: "#",
        test: "الفحص",
        location: "الموقع",
        // No "(د.ع)" here: every amount in the column now carries the IQD prefix itself.
        price: "السعر",
        subtotal: "المجموع الفرعي",
        adminFee: "رسوم إدارية",
        transportationFee: "رسوم النقل",
        free: "مجاناً",
        loyaltyDiscount: "خصم نقاط الولاء",
        pts: "نقطة",
        insuranceCoverage: "تغطية التأمين",
        promoDiscount: "خصم الرمز الترويجي",
        total: "الإجمالي",
        payment: "الدفع",
        method: "الطريقة",
        status: "الحالة",
        insurance: "التأمين",
        copayment: "نسبة المساهمة",
        deduction: "الخصم",
        noTests: "لا توجد فحوصات",
        notScheduled: "غير مجدول",
        homeVisit: "زيارة منزلية",
        lab: "مختبر",
      }
    : {
        preview: "Invoice Preview",
        print: "Print",
        close: "Close",
        invoice: "INVOICE",
        tagline: "Electronic Health Platform",
        patientDetails: "Patient Details",
        name: "Name",
        mobile: "Mobile",
        ageGender: "Age / Gender",
        yr: "yr",
        serviceDetails: "Service Details",
        typeLocation: "Type / Location",
        serviceDate: "Service Date",
        serviceTime: "Service Time",
        no: "#",
        test: "Test",
        location: "Location",
        // No "(IQD)" here: every amount in the column now carries the IQD prefix itself.
        price: "Price",
        subtotal: "Subtotal",
        adminFee: "Admin Fee",
        transportationFee: "Transportation Fee",
        free: "Free",
        loyaltyDiscount: "Loyalty Point Discount",
        pts: "pts",
        insuranceCoverage: "Insurance Coverage",
        promoDiscount: "Promo Discount",
        total: "Total",
        payment: "Payment",
        method: "Method",
        status: "Status",
        insurance: "Insurance",
        copayment: "Copayment",
        deduction: "Deduction",
        noTests: "No tests",
        notScheduled: "Not scheduled",
        homeVisit: "Home Visit",
        lab: "Lab",
      };

  const centerName =
    profile?.lab_name_english || profile?.lab_name_arabic || "TIRYAQ";
  const centerAvatar =
    profile?.avatar_presigned_url || profile?.avatar_url || null;
  const centerInitial =
    (centerName || "T").trim().charAt(0).toUpperCase() || "T";

  const fmtStatus = (s) =>
    s
      ? String(s)
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "";

  const handlePrint = () => {
    const node = invoiceRef.current;
    if (!node) return;
    const isRtl = i18n.language === "ar";
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;
    win.document
      .write(`<!DOCTYPE html><html dir="${isRtl ? "rtl" : "ltr"}" lang="${i18n.language}"><head><title>Lab Order Invoice - ${order?.bookingNumber || order?.id || ""}</title>
      <style>
        * { box-sizing: border-box; font-family: Arial, Helvetica, sans-serif; }
        body { margin: 24px; color: #333; }
        h2 { margin: 0; }
        table { width: 100%; border-collapse: collapse; }
        @media print { body { margin: 0; padding: 16px; } }
      </style></head><body>${node.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
    }, 250);
  };

  if (!open || !order) return null;

  // The invoice is addressed to the PATIENT (the account holder) — on a child booking that is
  // the guardian — exactly like the doctor app's consultation invoice, which prints the patient
  // record's name and falls back to the child's name only when the account holder's name is
  // unavailable. Kept in step with it so every provider bills the same person.
  const patientName =
    [
      order?.patient?.full_name,
      order?.patient?.surname,
    ]
      .filter(Boolean)
      .join(" ") ||
    order?.patient?.name ||
    [order?.child_name, order?.child_surname].filter(Boolean).join(" ") ||
    "N/A";

  const hasInsurance =
    order?.paymentMethod === "INSURANCE" ||
    (order?.insuranceStatus && order.insuranceStatus !== "N/A") ||
    order?.insuranceProviderId ||
    order?.insuranceDeductionAmount != null ||
    order?.insuranceCopaymentPercentage != null;

  const thStyle = (align) => ({
    background: "#f5f5f5",
    color: "#333",
    textAlign: align,
    padding: "8px 10px",
    fontSize: "12px",
    fontWeight: 600,
    borderBottom: "2px solid #ddd",
  });
  const rowStyle = {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    marginBottom: "4px",
  };
  const sectionTitle = {
    fontSize: "14px",
    fontWeight: 700,
    marginBottom: "8px",
    borderBottom: "1px solid #eee",
    paddingBottom: "4px",
  };
  const footCell = {
    padding: "8px 10px",
    fontSize: "13px",
    textAlign: isInvoiceAr ? "left" : "right",
  };
  const footLabel = { ...footCell, color: "#666" };

  return (
    <div
      onClick={onClose}
      dir={isInvoiceAr ? "rtl" : "ltr"}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "30px 16px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "12px",
          width: "100%",
          maxWidth: "800px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderBottom: "1px solid #e5e7eb",
            position: "sticky",
            top: 0,
            background: "#fff",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
            {invL.preview}
          </h4>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={handlePrint}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "#0cbdbd",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 20px",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              <FiPrinter size={16} /> {invL.print}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "#fff",
                color: "#333",
                border: "1px solid #ddd",
                borderRadius: "8px",
                padding: "8px 20px",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              {invL.close}
            </button>
          </div>
        </div>

        {/* Printable content */}
        <div ref={invoiceRef} style={{ padding: "22px 24px" }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "2px solid #0cbdbd",
              paddingBottom: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              {centerAvatar ? (
                <img
                  src={centerAvatar}
                  alt=""
                  style={{
                    width: "54px",
                    height: "54px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "1px solid #e6fafa",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "54px",
                    height: "54px",
                    borderRadius: "50%",
                    background: "#e6fafa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    color: "#0cbdbd",
                    fontSize: "20px",
                  }}
                >
                  {centerInitial}
                </div>
              )}
              <div>
                <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700 }}>
                  {centerName}
                </h2>
                <p style={{ margin: "2px 0", fontSize: "12px", color: "#999" }}>
                  {invL.tagline}
                </p>
                {profile?.mobile_number && (
                  <p
                    style={{ margin: "2px 0", fontSize: "12px", color: "#999" }}
                  >
                    {profile.mobile_number}
                  </p>
                )}
              </div>
            </div>
            <div style={{ textAlign: isInvoiceAr ? "left" : "right" }}>
              <div
                style={{ fontSize: "24px", fontWeight: 700, color: "#0cbdbd" }}
              >
                {invL.invoice}
              </div>
              <div style={{ fontSize: "13px", color: "#666" }}>
                {order?.bookingNumber
                  ? `#${order.bookingNumber}`
                  : order?.id
                    ? `#${order.id}`
                    : ""}
              </div>
              <div style={{ fontSize: "12px", color: "#999" }}>
                {fmtStatus(order?.status)}
              </div>
            </div>
          </div>

          {/* Patient details */}
          <div style={{ marginBottom: "18px" }}>
            <div style={sectionTitle}>{invL.patientDetails}</div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.name}</span>
              <span>{patientName}</span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.mobile}</span>
              <span>
                {order?.patient?.mobile_number ||
                  order?.patient?.mobileNumber ||
                  "N/A"}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.ageGender}</span>
              <span>
                {order?.patient?.age != null
                  ? `${order.patient.age} ${invL.yr}`
                  : "N/A"}{" "}
                · {order?.patient?.gender || "N/A"}
              </span>
            </div>
          </div>

          {/* Payment */}
          <div style={{ marginTop: "18px", marginBottom: "8px" }}>
            <div style={sectionTitle}>{invL.payment}</div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.method}</span>
              <span style={{ fontWeight: 600, textTransform: "uppercase" }}>
                {order?.paymentMethod || "N/A"}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.status}</span>
              <span
                style={{
                  fontWeight: 600,
                  color:
                    order?.paymentStatus === "COMPLETED"
                      ? "#00BC7D"
                      : "#e09200",
                }}
              >
                {order?.paymentStatus || "N/A"}
              </span>
            </div>
          </div>

          {/* Service details */}
          <div style={{ marginBottom: "18px" }}>
            <div style={sectionTitle}>{invL.serviceDetails}</div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.typeLocation}</span>
              <span>
                {order?.serviceType || invL.lab} ·{" "}
                {formatLocation(
                  order?.serviceLocation || order?.preferredLocation,
                )}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.serviceDate}</span>
              <span>
                {order?.scheduledDate
                  ? tzDateNumeric(new Date(order.scheduledDate))
                  : invL.notScheduled}
              </span>
            </div>
            <div style={rowStyle}>
              <span style={{ color: "#666" }}>{invL.serviceTime}</span>
              <span>{formatTime12h(order?.scheduledTime)}</span>
            </div>
          </div>

          {/* Tests table */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={thStyle(isInvoiceAr ? "right" : "left")}>
                  {invL.no}
                </th>
                <th style={thStyle(isInvoiceAr ? "right" : "left")}>
                  {invL.test}
                </th>
                <th style={thStyle(isInvoiceAr ? "right" : "left")}>
                  {invL.location}
                </th>
                <th style={thStyle(isInvoiceAr ? "left" : "right")}>
                  {invL.price}
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.tests.map((tst, i) => (
                <tr key={i}>
                  <td
                    style={{
                      padding: "8px 10px",
                      fontSize: "13px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {i + 1}
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      fontSize: "13px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {tst.name || tst.test_name || "N/A"}
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      fontSize: "13px",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {formatLocation(tst.location)}
                  </td>
                  <td
                    style={{
                      padding: "8px 10px",
                      fontSize: "13px",
                      borderBottom: "1px solid #eee",
                      textAlign: isInvoiceAr ? "left" : "right",
                    }}
                  >
                    {iqd(tst.price)}
                  </td>
                </tr>
              ))}
              {invoice.tests.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    style={{ textAlign: "center", padding: "10px" }}
                  >
                    {invL.noTests}
                  </td>
                </tr>
              )}
            </tbody>
            {/* The tfoot rows read as a single top-to-bottom calculation, same order as the
                doctor invoice (AppointmentInvoice.jsx):
                  tests                       (line items above)
                  + transportation fee              (home visit only)
                  = Subtotal                  (the base the admin fee % is charged on)
                  + admin fee
                  − insurance coverage        (INSURANCE-method payments only)
                  − loyalty point discount    (only when points were redeemed)
                  − promo discount            (only when a promo code was used)
                  = Total                     (what the patient actually paid)
                Example: 7,000 tests + 0 delivery = 7,000 → +350 admin → −80 loyalty
                = 7,270, which is exactly the amount charged. */}
            {(invoice.hasBackend || invoice.tests.length > 0) && (
              <tfoot>
                {invoice.hasHomeVisit && (
                  <tr>
                    <td
                      colSpan="3"
                      style={{ ...footLabel, borderTop: "2px solid #333" }}
                    >
                      {invL.transportationFee}
                    </td>
                    <td style={{ ...footCell, borderTop: "2px solid #333" }}>
                      {Number(invoice.transportationFee) > 0
                        ? `+ ${iqd(invoice.transportationFee)}`
                        : invL.free}
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    colSpan="3"
                    style={{
                      ...footLabel,
                      fontWeight: 600,
                      borderTop: invoice.hasHomeVisit
                        ? "1px solid #eee"
                        : "2px solid #333",
                    }}
                  >
                    {invL.subtotal}
                  </td>
                  <td
                    style={{
                      ...footCell,
                      fontWeight: 600,
                      borderTop: invoice.hasHomeVisit
                        ? "1px solid #eee"
                        : "2px solid #333",
                    }}
                  >
                    {iqd(invoice.subtotal)}
                  </td>
                </tr>
                {Number(invoice.adminFee) > 0 && (
                  <tr>
                    <td colSpan="3" style={footLabel}>
                      {invL.adminFee}
                      {Number(invoice.pct)
                        ? ` (${formatPercent(invoice.pct)}%)`
                        : ""}
                    </td>
                    <td style={footCell}>+ {iqd(invoice.adminFee)}</td>
                  </tr>
                )}
                {/* Insurance deduction applied at checkout (INSURANCE-method payments
                    only) — same position as the doctor invoice's coverage line. */}
                {Number(invoice.insuranceCoverage) > 0 && (
                  <tr>
                    <td colSpan="3" style={footLabel}>
                      {invL.insuranceCoverage}
                    </td>
                    <td style={{ ...footCell, color: "#00BC7D" }}>
                      − {iqd(invoice.insuranceCoverage)}
                    </td>
                  </tr>
                )}
                {/* Loyalty points redeemed — shown as a deduction (green, like the doctor
                    and pharmacy invoices) so the Total below reconciles with the amount
                    the patient was actually charged. */}
                {Number(invoice.loyaltyDiscount) > 0 && (
                  <tr>
                    <td colSpan="3" style={footLabel}>
                      {invL.loyaltyDiscount}
                      {invoice.loyaltyPointsUsed
                        ? ` (${invoice.loyaltyPointsUsed} ${invL.pts})`
                        : ""}
                    </td>
                    <td style={{ ...footCell, color: "#00BC7D" }}>
                      − {iqd(invoice.loyaltyDiscount)}
                    </td>
                  </tr>
                )}
                {/* Promo code discount — its own line so the derived Total still equals
                    what the patient pays. */}
                {Number(invoice.promoDiscount) > 0 && (
                  <tr>
                    <td colSpan="3" style={footLabel}>
                      {invL.promoDiscount}
                      {invoice.promoCode ? ` (${invoice.promoCode})` : ""}
                    </td>
                    <td style={{ ...footCell, color: "#00BC7D" }}>
                      − {iqd(invoice.promoDiscount)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td
                    colSpan="3"
                    style={{
                      padding: "10px",
                      fontWeight: 700,
                      borderTop: "1px solid #ccc",
                      textAlign: isInvoiceAr ? "left" : "right",
                    }}
                  >
                    {invL.total}
                  </td>
                  <td
                    style={{
                      padding: "10px",
                      fontWeight: 700,
                      borderTop: "1px solid #ccc",
                      textAlign: isInvoiceAr ? "left" : "right",
                      color: "#0cbdbd",
                    }}
                  >
                    {iqd(invoice.total)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>

          {/* Insurance box — only shown when the patient applied for insurance */}
          {hasInsurance && (
            <div
              style={{
                marginTop: "16px",
                padding: "14px 16px",
                border: "1px solid #d6f0f0",
                background: "#f2fbfb",
                borderRadius: "8px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#0cbdbd",
                  marginBottom: "10px",
                }}
              >
                {invL.insurance}
              </div>
              <div style={{ ...rowStyle, marginBottom: "6px" }}>
                <span style={{ color: "#666" }}>{invL.status}</span>
                <span style={{ fontWeight: 600 }}>
                  {order?.insuranceStatus || "N/A"}
                </span>
              </div>
              <div style={{ ...rowStyle, marginBottom: "6px" }}>
                <span style={{ color: "#666" }}>{invL.copayment}</span>
                <span style={{ fontWeight: 600, color: "#e09200" }}>
                  {order?.insuranceCopaymentPercentage != null
                    ? `${formatPercent(order.insuranceCopaymentPercentage)}%`
                    : "N/A"}
                </span>
              </div>
              <div style={{ ...rowStyle, marginBottom: "6px" }}>
                <span style={{ color: "#666" }}>{invL.deduction}</span>
                <span style={{ fontWeight: 600 }}>
                  {order?.insuranceDeductionAmount != null
                    ? iqd(order.insuranceDeductionAmount)
                    : "N/A"}
                </span>
              </div>
            </div>
          )}

          {/* Footer — show the center name (not the platform name). */}
          <div
            style={{
              marginTop: "26px",
              textAlign: "center",
              fontSize: "12px",
              color: "#999",
              borderTop: "1px solid #eee",
              paddingTop: "12px",
            }}
          >
            {`${isInvoiceAr ? "شكراً لاختياركم" : "Thank you for choosing"} ${centerName}.`}
          </div>
        </div>
      </div>
    </div>
  );
}
